import { APIResponseType } from 'app/apiService/types';
import apiService from '..';

// Dealer Special interface
export interface DealerSpecial {
  _id: string;
  dealership_id: string;
  special_headline: string;
  special_description: string;
  start_date: string;
  end_date: string;
  applicable_model: string;
  disclaimer_text: string;
  created_at?: string;
  updated_at?: string;
}

interface DealerSpecialPayload extends Partial<DealerSpecial> {
  special_headline: string;
  special_description: string;
  start_date: string;
  end_date: string;
  applicable_model: string;
  disclaimer_text: string;
}

interface GetAllDealerSpecialsParams {
  skip?: number;
  limit?: number;
  search?: string;
  sort?: string;
  filters?: string;
  dealer?: string;
}

class DealerSpecialsService {
  static ENDPOINT = 'api/v2/dealer-special';

  static async getDealerSpecial(id: string): Promise<APIResponseType> {
    if (!id) throw new Error('Dealer Special ID is required to fetch details');
    try {
      const response = await apiService.get<APIResponseType>(`${this.ENDPOINT}/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async getAllDealerSpecials(params?: GetAllDealerSpecialsParams): Promise<APIResponseType> {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
        if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.sort) queryParams.append('sort', params.sort);
        if (params.filters) queryParams.append('filters', params.filters);
        if (params.dealer) queryParams.append('dealer', params.dealer);
      }
      const queryString = queryParams.toString();
      const url = queryString ? `${this.ENDPOINT}?${queryString}` : this.ENDPOINT;
      const response = await apiService.get<APIResponseType>(url);
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async addDealerSpecial(payload: DealerSpecialPayload): Promise<APIResponseType> {
    try {
      const response = await apiService.post<APIResponseType>(`${this.ENDPOINT}`, payload);
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async deleteDealerSpecial(id: string): Promise<APIResponseType> {
    if (!id) throw new Error('Dealer Special ID is required for deletion');
    try {
      const response = await apiService.delete<APIResponseType>(`${this.ENDPOINT}/${id}`);
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async editDealerSpecial(id: string, payload: Partial<DealerSpecialPayload>): Promise<APIResponseType> {
    if (!id) throw new Error('Dealer Special ID is required for editing');
    if (!Object.keys(payload).length) throw new Error('Edit payload cannot be empty');
    try {
      const response = await apiService.put<APIResponseType>(`${this.ENDPOINT}/${id}`, payload);
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }
}

export default DealerSpecialsService; 